import React from 'react';
import PropTypes from 'prop-types';
import { BlushingModal, Button } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import useSearch from 'hooks/store/useSearch';
import useUserPreferences from 'hooks/useUserPreferences';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { getBaseEligibility, getResultsEligibility } from 'utils/quickPurchaseEligibility';
import checkIconSrc from 'images/purchases/quick-purchase-check.png';
import quickPurchaseIconSrc from 'images/purchases/quick-purchase.png';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';

/**
 * QuickPurchaseIntroModal component.
 * Shows an introductory modal explaining the Quick Purchase feature.
 * Opens when the user clicks the quick purchase banner.
 * @param {object} props - trips, isRoundTrip, isExchange, isQuickPurchaseModalOpen, isOpen, onClose.
 * @param {Array} props.trips - List of trip objects from search results.
 * @param {boolean} props.isRoundTrip - Whether the current search is round trip.
 * @param {boolean} props.isExchange - Whether the current flow is an exchange.
 * @param {boolean} props.isQuickPurchaseModalOpen - Whether the Quick Purchase modal is open (prevents overlap).
 * @param {boolean} props.isOpen - Whether the banner was clicked to open this modal.
 * @param {Function} props.onClose - Called when the modal is closed.
 * @returns {JSX.Element|null} The intro modal or null.
 */
const QuickPurchaseIntroModal = ({
  trips,
  isRoundTrip,
  isExchange,
  isQuickPurchaseModalOpen,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('purchase');
  const features = useWhitelabelFeatures();
  const quickPurchaseEnabled = useExperimentalFeature('QUICK_PURCHASE_ENABLED');
  const { userIsLoggedInWithAnyLoyaltyProgram, isQuickPurchaseLoyaltySettled = true } =
    useLoyaltyPrograms();
  const { passengers: searchPassengers, departure: searchDeparture } = useSearch();
  const isBulkTicket = searchDeparture?.routeType === 'bulk_ticket';
  const { passengers: frequentPassengers } = useUserPreferences();

  const { eligible } = getBaseEligibility({
    quickPurchaseEnabled,
    searchPassengers,
    frequentPassengers,
    features,
    isRoundTrip,
    isExchange,
    isBulkTicket,
    isQuickPurchaseLoyaltySettled,
    userIsLoggedInWithAnyLoyaltyProgram,
  });

  const isResultsEligible = getResultsEligibility(trips, eligible, features);
  const shouldShow = isOpen && isResultsEligible && !isQuickPurchaseModalOpen;

  if (!shouldShow) return null;

  return (
    <BlushingModal
      defaultOpened
      onCloseModal={onClose}
      responsiveSize="auto"
      contentHeight="auto"
      closeOnOverlayClick={false}
      removeContentPadding
    >
      <div className="flex flex-col p-8 gap-6 max-w-m mx-auto">
        <div className="flex gap-2">
          <img src={quickPurchaseIconSrc} alt="" className="mr-1 mt-2 h-[50px] w-auto" />
          <div className="flex flex-col gap">
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">
              {t('title.quick_purchase_intro_title')}
            </h2>
            <p className="text-sm text-gray-500">{t('title.quick_purchase_intro_subtitle')}</p>
          </div>
        </div>

        <ul className="flex flex-col gap-3">
          <li className="flex items-start gap-3">
            <img src={checkIconSrc} alt="" className="mt-0.5 flex-shrink-0 w-5 h-5" />
            <span className="text-sm font-bold text-gray-700">
              {t('title.quick_purchase_intro_check_seat')}
            </span>
          </li>
          <li className="flex items-start gap-3">
            <img src={checkIconSrc} alt="" className="mt-0.5 flex-shrink-0 w-5 h-5" />
            <span className="text-sm font-bold text-gray-700">
              {t('title.quick_purchase_intro_check_passengers')}
            </span>
          </li>
        </ul>

        <p className="text-xs text-gray-500">{t('title.quick_purchase_intro_note')}</p>

        <Button variant="primary" onClick={onClose} isRounded>
          {t('title.quick_purchase_intro_cta')}
        </Button>
      </div>
    </BlushingModal>
  );
};

QuickPurchaseIntroModal.propTypes = {
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      allowsSeatSelection: PropTypes.bool,
      hasConnections: PropTypes.bool,
    }),
  ).isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  isExchange: PropTypes.bool.isRequired,
  isQuickPurchaseModalOpen: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default QuickPurchaseIntroModal;
